import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCopy } from '@fortawesome/free-solid-svg-icons';

import HoverTooltip from '@/components/tooltip/HoverTooltip.jsx';

import './CopyableId.css';

/**
 * An id shown in small monospace with a copy button - e.g. a game's id beside its name, which the SDK, support and
 * the API all ask for. A span with role="button" rather than a <button>: it sits inside the game switcher's rows,
 * which are buttons themselves. Copying never selects the row it sits in. What it copies, then "Copied", shows on the
 * standard hover, not the browser's own tooltip.
 *
 * `variant="badge"` is a key-value badge: [ ID | 12378637 ⧉ ] - a tinted "ID" tab, then the id and its copy icon.
 */
const CopyableId = ({ id, label = 'ID', variant = 'chip', className = '' }) => {
    const [copied, setCopied] = useState(false);
    const timer = useRef(null);
    useEffect(() => () => clearTimeout(timer.current), []);
    if (!id) return null;

    const copy = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(String(id));
            setCopied(true);
            clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard blocked - the id stays visible and selectable */ }
    };

    return (
        <HoverTooltip className="copyable-id-anchor" content={copied ? 'Copied' : `Copy ${label.toLowerCase()} ${id}`}>
            <span
                className={`copyable-id copyable-id-${variant} ${copied ? 'copied' : ''} ${className}`}
                role="button"
                tabIndex={0}
                aria-label={copied ? `${label} copied` : `Copy ${label.toLowerCase()} ${id}`}
                onClick={copy}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') copy(e);
                }}
            >
                {variant === 'badge' ? (
                    <>
                        <span className="copyable-id-key" aria-hidden="true">ID</span>
                        <span className="copyable-id-body">
                            <span className="copyable-id-value">{id}</span>
                            <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="copyable-id-icon" />
                        </span>
                    </>
                ) : (
                    <>
                        <span className="copyable-id-value">{id}</span>
                        <FontAwesomeIcon icon={copied ? faCheck : faCopy} className="copyable-id-icon" />
                    </>
                )}
            </span>
        </HoverTooltip>
    );
};

export default CopyableId;
